import { useMemo, useState } from 'react'
import {Copy, RotateCcw} from 'lucide-react'
import toast from 'react-hot-toast'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import AdSlot from '@/components/AdSlot'
import { formatCurrency, formatNumber } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'
import { coinPresets, gradeMultipliers } from '@/data/coinPresets'

const goldPresets = coinPresets.filter(p => p.metal === 'gold')

const faqItems = [
  { question: 'What is the American Gold Eagle made of?', answer: 'The American Gold Eagle 1 oz coin contains exactly 1 troy ounce of pure gold, but its total weight is 1.0909 oz because it is alloyed with copper and silver for durability (91.67% purity, or 22-karat gold).' },
  { question: 'Why is the strike premium lower for gold coins than silver?', answer: 'Gold bullion coins typically carry lower percentage premiums over spot because the underlying metal value is much higher relative to minting costs, making the premium a smaller proportion of total coin value.' },
  { question: 'How does Proof 70 Cameo grading affect value?', answer: 'Proof 70 Cameo is the highest possible grade for proof coins, indicating a flawless strike with frosted design elements against a mirror-like background. These command the largest numismatic premiums of any grade tier.' },
]

function GoldEagleMint() {
  const [presetId, setPresetId] = useState(goldPresets[0].id)
  const [spotPrice, setSpotPrice] = useState(goldPresets[0].defaultSpot)
  const [weightOz, setWeightOz] = useState(goldPresets[0].weightOz)
  const [purity, setPurity] = useState(goldPresets[0].purity * 100)
  const [gradeId, setGradeId] = useState('ms60')

  const preset = goldPresets.find(p => p.id === presetId) ?? goldPresets[0]
  const grade = gradeMultipliers.find(g => g.id === gradeId) ?? gradeMultipliers[0]

  const applyPreset = (id: string) => {
    const p = goldPresets.find(x => x.id === id) ?? goldPresets[0]
    setPresetId(id)
    setSpotPrice(p.defaultSpot)
    setWeightOz(p.weightOz)
    setPurity(p.purity * 100)
  }

  const results = useMemo(() => {
    const pureWeight = weightOz * (purity / 100)
    const meltValue = pureWeight * spotPrice
    const strikePremium = meltValue * (preset.strikePremiumPct / 100)
    const baseMintValue = meltValue + strikePremium
    const gradePremium = baseMintValue * (grade.multiplier - 1)
    const collectorValue = baseMintValue + gradePremium
    return { pureWeight, meltValue, strikePremium, gradePremium, collectorValue }
  }, [weightOz, purity, spotPrice, preset.strikePremiumPct, grade.multiplier])

  const handleReset = () => {
    applyPreset(goldPresets[0].id)
    setGradeId('ms60')
    toast.success('Reset to default values')
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(`Mint Calculator - Gold Eagle Mint Value\nCoin: ${preset.name}\nGrade: ${grade.label}\nMelt Value: ${formatCurrency(results.meltValue)}\nEstimated Collector Value: ${formatCurrency(results.collectorValue)}`)
    toast.success('Summary copied to clipboard')
  }

  return (
    <>
      <Seo
        title="Gold Eagle Mint Value Calculator - Melt & Collector Value"
        description="Calculate the melt value and estimated numismatic value of American Gold Eagles, Krugerrands, Gold Buffalos, and Canadian Gold Maple Leafs based on spot price and grade."
        path="/calculators/gold-eagle-mint"
        schema={[
          softwareApplicationSchema('Gold Eagle Mint Value Calculator', 'Calculate melt value and numismatic collector value of gold bullion coins.', 'https://www.mintcalculator.com/calculators/gold-eagle-mint'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Gold Eagle Mint Value Calculator', path: '/calculators/gold-eagle-mint' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Gold Eagle Mint Value Calculator', path: '/calculators/gold-eagle-mint' }]} />
        <SectionHeading
          eyebrow="Numismatic Coin & Bullion"
          title="Gold Eagle Mint Value Calculator"
          description="Estimate the melt floor value and numismatic collector value of gold bullion coins based on spot price, purity, weight, and condition grade."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-300">Coin preset</span>
              <select
                value={presetId}
                onChange={e => applyPreset(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2.5 text-sm text-slate-50 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
              >
                {goldPresets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <NumberInput label="Gold spot price" value={spotPrice} onChange={setSpotPrice} prefix="$" suffix="/oz" step={1} accent="amber" />
            <NumberInput label="Coin weight" value={weightOz} onChange={setWeightOz} suffix="oz" step={0.001} accent="cyan" />
            <NumberInput label="Purity" value={purity} onChange={setPurity} suffix="%" step={0.01} accent="teal" />
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-300">Condition grade</span>
              <select
                value={gradeId}
                onChange={e => setGradeId(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2.5 text-sm text-slate-50 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              >
                {gradeMultipliers.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
              </select>
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={handleCopy} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <Copy className="h-4 w-4" />
                Copy Summary
              </button>
              <button onClick={handleReset} className="flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:border-emerald-500/50 hover:text-emerald-400">
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Pure Metal Weight" value={`${formatNumber(results.pureWeight, 4)} oz`} accent="cyan" />
              <StatCard label="Raw Melt Floor Value" value={formatCurrency(results.meltValue)} accent="slate" />
              <StatCard label="Mint Strike Premium" value={formatCurrency(results.strikePremium)} accent="teal" />
              <StatCard label="Grade Premium" value={formatCurrency(results.gradePremium)} accent="amber" />
              <StatCard label="Est. Collector Value" value={formatCurrency(results.collectorValue)} accent="emerald" size="lg" sublabel={grade.label} />
            </div>
            <div className="mt-4">
              <AdSlot variant="inline" />
            </div>
          </div>
        </div>

        <div className="mt-8">
          <DisclaimerNote />
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default GoldEagleMint
